import { supabase } from '@shared/lib/supabase/client'
import type { User, Session } from '@supabase/supabase-js'

export interface SignInCredentials {
  email: string
  password: string
}

export interface SignUpCredentials {
  email: string
  password: string
  fullName?: string
  organization?: string
  functie?: string
  telefoonnummer?: string
}

export interface AuthResponse {
  user?: User | null
  session?: Session | null
  error?: string
}

class AuthService {
  async signIn({ email, password }: SignInCredentials): Promise<AuthResponse> {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      if (error) throw error

      return {
        user: data.user,
        session: data.session,
      }
    } catch (error) {
      console.error('Sign in error:', error)
      return {
        error: error instanceof Error ? error.message : 'Er is een fout opgetreden bij het inloggen',
      }
    }
  }

  async signUp({ email, password, fullName, organization, functie, telefoonnummer }: SignUpCredentials): Promise<AuthResponse> {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
            organization,
            functie,
            telefoonnummer,
          },
        },
      })

      if (error) throw error

      // Note: User profile is automatically created by database trigger (see migration 20251010152754)
      // The trigger extracts full_name, organization, functie, and telefoonnummer from raw_user_meta_data

      return {
        user: data.user,
        session: data.session,
      }
    } catch (error) {
      console.error('Sign up error:', error)
      return {
        error: error instanceof Error ? error.message : 'Er is een fout opgetreden bij het registreren',
      }
    }
  }

  async signOut(): Promise<{ error?: string }> {
    try {
      const { error } = await supabase.auth.signOut()
      if (error) throw error
      return {}
    } catch (error) {
      console.error('Sign out error:', error)
      return {
        error: error instanceof Error ? error.message : 'Er is een fout opgetreden bij het uitloggen',
      }
    }
  }

  async resetPassword(email: string): Promise<{ error?: string }> {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      })

      if (error) throw error
      return {}
    } catch (error) {
      console.error('Password reset error:', error)
      return {
        error: error instanceof Error ? error.message : 'Er is een fout opgetreden bij het resetten van het wachtwoord',
      }
    }
  }

  async verifyPassword(password: string): Promise<{ isValid: boolean; error?: string }> {
    try {
      const { data: { user } } = await supabase.auth.getUser()

      if (!user?.email) {
        return {
          isValid: false,
          error: 'Geen gebruiker gevonden'
        }
      }

      // Re-authenticate user with current password to verify it's correct
      const { error } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: password,
      })

      if (error) {
        return {
          isValid: false,
          error: 'Onjuist wachtwoord'
        }
      }

      return { isValid: true }
    } catch (error) {
      console.error('Password verification error:', error)
      return {
        isValid: false,
        error: error instanceof Error ? error.message : 'Er is een fout opgetreden bij het verifiëren van het wachtwoord',
      }
    }
  }

  async updatePassword(currentPassword: string, newPassword: string): Promise<{ success?: boolean; error?: string }> {
    try {
      // First verify the current password
      const verificationResult = await this.verifyPassword(currentPassword)

      if (!verificationResult.isValid) {
        return {
          success: false,
          error: verificationResult.error || 'Huidig wachtwoord is onjuist'
        }
      }

      // Update to the new password
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      })

      if (error) throw error

      return { success: true }
    } catch (error) {
      console.error('Password update error:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Er is een fout opgetreden bij het updaten van het wachtwoord',
      }
    }
  }

  async getSession(): Promise<Session | null> {
    const { data } = await supabase.auth.getSession()
    return data.session
  }

  async getUser(): Promise<User | null> {
    const { data } = await supabase.auth.getUser()
    return data.user
  }

  async refreshSession(): Promise<{ session: Session | null; error?: string }> {
    try {
      const { data, error } = await supabase.auth.refreshSession()
      if (error) throw error
      return { session: data.session }
    } catch (error) {
      console.error('Session refresh error:', error)
      return {
        session: null,
        error: error instanceof Error ? error.message : 'Er is een fout opgetreden bij het vernieuwen van de sessie',
      }
    }
  }

  onAuthStateChange(callback: (event: string, session: Session | null) => void) {
    return supabase.auth.onAuthStateChange((event, session) => {
      callback(event, session)
    })
  }
}

export const authService = new AuthService()